import { History as HistoryIcon, Loader2 } from '@/lib/icons'
import type { ActivityLogItem } from '@ayub/shared'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { formatRelativeUz } from '@/lib/relative-date'
import { describeActivity, describeActor } from './describe'
import { HistoryErrorBanner, HistoryRetryState } from './ErrorStates'
import { useActivityHistory } from './hooks'

export interface HistorySectionProps {
  /** `ActivityLog.entityType` — `'blogger'` yoki `'video'` (boshqa turlar hozircha bu
   * bo'lim orqali ko'rsatilmaydi, faqat kartochka ichida turgan yagona yozuv uchun). */
  entityType: string
  entityId: number
}

/**
 * "Tarix" bo'limi — bloger/video kartochkasi pastida, `docs/05-backend-schema.md`dagi
 * `GET /api/activity` ustiga qurilgan. Qayta ishlatiladigan: `BloggerDetail.tsx` va
 * `VideoDetail.tsx` ikkalasi ham xuddi shu komponentni faqat `entityType`/`entityId`
 * bilan chaqiradi (`components/payouts/`dagi bo'lim naqshi bilan bir xil — bitta
 * komponent, ikkita joyda ulanadi).
 *
 * Vaqt tartibi — yangi tepada: server allaqachon `createdAt DESC` bilan qaytaradi
 * (`services/activity.ts`, `prisma/schema.prisma`dagi `@@index([entityType, entityId,
 * createdAt])`), shuning uchun bu yerda qayta saralash SHART EMAS.
 */
export function HistorySection({ entityType, entityId }: HistorySectionProps) {
  const { items, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, error, refetch } =
    useActivityHistory(entityType, entityId)

  const firstLoadFailed = error !== null && items.length === 0

  return (
    <section className="flex flex-col gap-2 rounded-card border border-app-border bg-app-card p-4">
      <h2 className="inline-flex items-center gap-2 text-sm font-semibold">
        <HistoryIcon size={16} className="text-brand" aria-hidden="true" />
        Tarix
      </h2>

      {isLoading && <HistorySkeleton />}

      {!isLoading && firstLoadFailed && (
        <HistoryRetryState error={error} onRetry={() => void refetch()} />
      )}

      {!isLoading && !firstLoadFailed && items.length === 0 && (
        <p className="text-sm text-app-hint">Hali amallar yo&apos;q</p>
      )}

      {!isLoading && items.length > 0 && (
        <>
          <ul className="flex flex-col divide-y divide-app-border">
            {items.map((item) => (
              <HistoryRow key={item.id} item={item} />
            ))}
          </ul>

          {/* Yozuvlar allaqachon ekranda, lekin "Yana ko'rsatish" muvaffaqiyatsiz bo'ldi —
              butun bo'limni xato holatiga almashtirmaymiz, faqat yuqori banner ko'rsatamiz. */}
          {error && <HistoryErrorBanner error={error} onRetry={() => void refetch()} />}

          {hasNextPage && (
            <Button
              variant="secondary"
              size="sm"
              className="self-center"
              onClick={() => void fetchNextPage()}
              disabled={isFetchingNextPage}
            >
              {isFetchingNextPage && (
                <Loader2 size={14} className="animate-spin" aria-hidden="true" />
              )}
              Yana ko&apos;rsatish
            </Button>
          )}
        </>
      )}
    </section>
  )
}

function HistoryRow({ item }: { item: ActivityLogItem }) {
  return (
    <li className="flex flex-col gap-0.5 py-2 text-sm">
      <span className="text-app-text">{describeActivity(item)}</span>
      <span className="text-xs text-app-hint">
        {formatRelativeUz(item.createdAt)} · {describeActor(item)}
      </span>
    </li>
  )
}

/** Yakuniy tarkib bilan bir xil o'lchamdagi joy egallovchi — layout shift bo'lmasin. */
function HistorySkeleton() {
  return (
    <div className="flex flex-col gap-2" aria-busy="true">
      <Skeleton className="h-10" />
      <Skeleton className="h-10" />
      <Skeleton className="h-10" />
    </div>
  )
}
